import { jsxs, jsx } from "react/jsx-runtime";
import { forwardRef } from "react";
import styled, { css } from "styled-components";
import { colors, r4, s4, s8, s16 } from "@ardoq/design-tokens";
import { focusableCSS, errorCSS, warningCSS, Space } from "@ardoq/style-helpers";
import { isString, isNumber, capitalize } from "lodash";
import { ICON_SELECTOR, IconSize, SpinnerIcon, Icon } from "@ardoq/icons";
import { button } from "@ardoq/typography";
import { TextOverflow } from "@ardoq/popovers";
var ButtonType = /* @__PURE__ */ ((ButtonType2) => {
  ButtonType2["SECONDARY"] = "SECONDARY";
  ButtonType2["PRIMARY"] = "PRIMARY";
  ButtonType2["TERTIARY"] = "TERTIARY";
  ButtonType2["DANGER"] = "DANGER";
  ButtonType2["BRAND"] = "BRAND";
  ButtonType2["GHOST"] = "GHOST";
  ButtonType2["INPUT_LOOK"] = "INPUT_LOOK";
  return ButtonType2;
})(ButtonType || {});
var ButtonSize = /* @__PURE__ */ ((ButtonSize2) => {
  ButtonSize2["SMALL"] = "SMALL";
  ButtonSize2["REGULAR"] = "REGULAR";
  return ButtonSize2;
})(ButtonSize || {});
const composeCssColorBlock = ({
  color,
  border,
  backgroundColor,
  iconColor,
  boxShadow
}) => css`
  ${color && css`
    color: ${color};
  `};
  ${backgroundColor && css`
    background-color: ${backgroundColor};
  `}
  ${(iconColor || color) && css`
    ${ICON_SELECTOR} {
      color: ${iconColor || color};
    }
  `}
  ${boxShadow && css`
    box-shadow: ${boxShadow};
  `}
  ${border && css`
    border: ${border};
  `}
`;
const composeCssStatesColorBlock = (colorMap, isActive) => css`
  ${composeCssColorBlock(colorMap.default)}
  &:hover {
    ${composeCssColorBlock(colorMap.hover)}
  }
  &:active {
    ${composeCssColorBlock(colorMap.active)}
  }
  &:disabled {
    ${composeCssColorBlock(colorMap.disabled)}
  }
  ${isActive && composeCssColorBlock(colorMap.selected || colorMap.active)}
`;
const TERTIARY_BUTTON_COLOR_MAP = {
  default: {
    color: colors.textDefault,
    backgroundColor: colors.surfaceActionTertiary,
    iconColor: colors.icon,
    border: `1px solid ${colors.borderAction}`
  },
  hover: {
    backgroundColor: colors.surfaceActionTertiaryHover,
    border: `1px solid ${colors.borderActionHover}`
  },
  active: {
    backgroundColor: colors.surfaceActionTertiaryPressed,
    iconColor: colors.iconActionHover,
    border: `1px solid ${colors.borderActionActive}`
  },
  disabled: {
    backgroundColor: colors.surfaceActionTertiaryDisabled,
    color: colors.textDisabled,
    iconColor: colors.iconActionDisabled,
    border: `1px solid transparent`
  }
};
const GHOST_BUTTON_COLOR_MAP = {
  default: {
    color: colors.textDefault,
    backgroundColor: colors.surfaceActionGhost,
    iconColor: colors.icon
  },
  hover: {
    backgroundColor: colors.surfaceActionGhostHover
  },
  active: {
    color: colors.textModerate,
    backgroundColor: colors.surfaceActionGhostPressed,
    iconColor: colors.iconActionHover
  },
  selected: {
    color: colors.textAction,
    iconColor: colors.iconActionActive,
    backgroundColor: colors.surfaceActionGhostPressed
  },
  disabled: {
    backgroundColor: colors.surfaceActionGhostDisabled,
    color: colors.textDisabled,
    iconColor: colors.iconActionDisabled
  }
};
const SECONDARY_BUTTON_COLOR_MAP = {
  default: {
    color: colors.textDefault,
    backgroundColor: colors.surfaceActionSecondary,
    iconColor: colors.iconAction
  },
  hover: {
    backgroundColor: colors.surfaceActionSecondaryHover
  },
  active: {
    backgroundColor: colors.surfaceActionSecondaryPressed,
    iconColor: colors.iconActionHover
  },
  disabled: {
    backgroundColor: colors.surfaceActionSecondaryDisabled,
    color: colors.textDisabled,
    iconColor: colors.iconActionDisabled
  }
};
const PRIMARY_BUTTON_COLOR_MAP = {
  default: {
    color: colors.textOnInvertedStrong,
    backgroundColor: colors.surfaceActionPrimary
  },
  hover: {
    backgroundColor: colors.surfaceActionPrimaryHover
  },
  active: {
    backgroundColor: colors.surfaceActionPrimaryPressed
  },
  disabled: {
    backgroundColor: colors.surfaceActionPrimaryDisabled
  }
};
const DANGER_BUTTON_COLOR_MAP = {
  default: {
    color: colors.textOnInvertedStrong,
    backgroundColor: colors.surfaceActionDelete
  },
  hover: {
    backgroundColor: colors.surfaceActionDeleteHover
  },
  active: {
    backgroundColor: colors.surfaceActionDeletePressed
  },
  disabled: {
    backgroundColor: colors.surfaceActionDeleteDisabled
  }
};
const BRAND_BUTTON_COLOR_MAP = {
  default: {
    color: colors.textOnInvertedStrong,
    backgroundColor: colors.surfaceActionBrand
  },
  hover: {
    backgroundColor: colors.surfaceActionBrandHover
  },
  active: {
    backgroundColor: colors.surfaceActionBrandPressed
  },
  disabled: {
    backgroundColor: colors.surfaceActionBrandDisabled
  }
};
const INPUT_LOOK_BUTTON_COLOR_MAP = {
  default: {
    backgroundColor: colors.surfaceActionGhost,
    boxShadow: `0px 0px 0px 1px ${colors.borderAction}`
  },
  hover: {
    boxShadow: `0px 0px 0px 1px ${colors.borderActionHover}`
  },
  active: {
    boxShadow: `0px 0px 0px 2px ${colors.borderActionActive}`
  },
  disabled: {
    backgroundColor: colors.surfaceDisabled
  }
};
const BUTTON_COLOR_MAP = {
  [ButtonType.SECONDARY]: SECONDARY_BUTTON_COLOR_MAP,
  [ButtonType.PRIMARY]: PRIMARY_BUTTON_COLOR_MAP,
  [ButtonType.DANGER]: DANGER_BUTTON_COLOR_MAP,
  [ButtonType.BRAND]: BRAND_BUTTON_COLOR_MAP,
  [ButtonType.GHOST]: GHOST_BUTTON_COLOR_MAP,
  [ButtonType.TERTIARY]: TERTIARY_BUTTON_COLOR_MAP,
  [ButtonType.INPUT_LOOK]: INPUT_LOOK_BUTTON_COLOR_MAP
};
const MEDIUM_BUTTON_MINIMUM_HEIGHT = 36;
const isSmall = (buttonSize) => buttonSize === ButtonSize.SMALL;
const ButtonLoadingIconWrapper = styled.div`
  position: absolute;
  width: 100%;
  top: calc(50% - 12px);
`;
const OPACITY_TRANSITION = "opacity 0.2s ease-in-out";
const UnstyledButton = styled.button`
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border: none;
  padding: 0;
  margin: 0;
  background: transparent;
  box-shadow: none;
  /* Inherit font properties from parent element */
  font: inherit; /* Includes family, size, weight, line-height, etc. */
  /* Inherit text color from parent element */
  color: inherit;
  /* Inherit text alignment */
  text-align: inherit;
  text-decoration: none;
  /* Prevent any default text transformation */
  text-transform: none;
  outline: none;
  ${focusableCSS};
`;
const ButtonStyle = styled.button`
  ${button};
  cursor: pointer;
  border-radius: ${r4};
  min-height: ${({ $buttonSize }) => isSmall($buttonSize) ? "32px" : `${MEDIUM_BUTTON_MINIMUM_HEIGHT}px`};
  padding: ${({ $buttonSize }) => isSmall($buttonSize) ? "2px 0" : `${s4} 0`};
  border: 1px solid transparent;
  transition: all 0.2s ease-in-out;
  vertical-align: bottom;
  font-weight: 500;
  user-select: none;

  ${({ $overflowMethod }) => $overflowMethod === "ellipsis" && css`
      max-width: 100%;
    `};

  ${focusableCSS};

  ${({ $buttonType, $isActive }) => composeCssStatesColorBlock(BUTTON_COLOR_MAP[$buttonType], $isActive)};

  ${({ $isDisabled, $popoverId }) => $isDisabled ? $popoverId ? css`
            pointer-events: revert;
          ` : css`
            pointer-events: none;
            &[data-tooltip-text] {
              pointer-events: revert;
            }
          ` : ""}

  ${({ $isFlexible }) => $isFlexible && css`
      width: 100%;
      ${ButtonContent} {
        justify-content: center;
      }
    `}

  ${({ $isLoading }) => css`
    ${$isLoading && css`
      pointer-events: none;
    `}
    ${ButtonContent} {
      transition: ${OPACITY_TRANSITION};
      opacity: ${$isLoading ? 0 : 1};
    }
    ${ButtonLoadingIconWrapper} {
      transition: ${OPACITY_TRANSITION};
      opacity: ${$isLoading ? 1 : 0};
      pointer-events: none;
    }
  `}
  ${({ $hasError }) => $hasError && errorCSS}
  ${({ $hasError, $hasWarning }) => !$hasError && $hasWarning && warningCSS}
`;
const BUTTON_SELECTOR = ButtonStyle.toString();
const ButtonText = styled.div``;
const ButtonContent = styled.div`
  display: flex;
  align-items: center;

  ${({ $buttonSize, $isFlexible }) => css`
    ${ICON_SELECTOR} {
      flex-shrink: 0;
      margin: 0 ${isSmall($buttonSize) && !$isFlexible ? s4 : s8};

      /*
        Case if Icon is the only button element (essentially it's IconButton).
        We want to have icon buttons exactly square that's why we are using this
        custom 5px spacing.
      */
      &:first-child:last-child {
        margin: 0 5px;
      }

      /*
        This is a special case where we set icon size
        with parent's styles. It's because button size
        designate icon size. That's why I have to use
        here !important (in order to overwite inline styles)
      */
      font-size: ${isSmall($buttonSize) ? IconSize.MEDIUM : IconSize.REGULAR}px !important;
      width: ${isSmall($buttonSize) ? IconSize.MEDIUM : IconSize.REGULAR}px !important;
      min-width: ${isSmall($buttonSize) ? IconSize.MEDIUM : IconSize.REGULAR}px !important;
      height: ${isSmall($buttonSize) ? IconSize.MEDIUM : IconSize.REGULAR}px !important;
    }

    ${ButtonText} {
      &:last-child {
        margin-right: ${isSmall($buttonSize) || $isFlexible ? s8 : s16};
      }
      &:first-child {
        margin-left: ${isSmall($buttonSize) || $isFlexible ? s8 : s16};
      }
    }
  `}
`;
const reactNodeToArray = (node) => (Array.isArray(node) ? node : [node]).flat().filter(Boolean);
const wrapTextNodes = (node, options = {}) => reactNodeToArray(node).map(
  (node2, index) => isString(node2) || isNumber(node2) ? /* @__PURE__ */ jsx(
    ButtonText,
    {
      as: options.overflowMethod === "ellipsis" ? TextOverflow : "div",
      children: node2
    },
    index
  ) : node2
);
const Button = forwardRef(
  ({
    buttonSize = ButtonSize.REGULAR,
    isFlexible,
    overflowMethod = "break-spaces",
    hasWarning,
    hasError,
    buttonType = ButtonType.TERTIARY,
    isActive,
    isDisabled,
    isLoading,
    popoverId,
    dataTestId,
    onClick,
    ...buttonProps
  }, ref) => {
    const hasOnClickHandler = Boolean(onClick);
    return /* @__PURE__ */ jsxs(
      ButtonStyle,
      {
        ref,
        ...hasOnClickHandler ? { type: "button" } : {},
        ...buttonProps,
        ...isLoading ? {
          "aria-disabled": true,
          "aria-label": "loading",
          "aria-busy": true
        } : {},
        onClick: (evt) => {
          if (isLoading) return;
          onClick == null ? void 0 : onClick(evt);
        },
        $popoverId: popoverId,
        $isActive: isActive,
        $isDisabled: isDisabled,
        $isLoading: isLoading,
        $buttonType: buttonType,
        $hasError: hasError,
        $hasWarning: hasWarning,
        $overflowMethod: overflowMethod,
        $isFlexible: isFlexible,
        $buttonSize: buttonSize,
        disabled: isDisabled,
        "data-test-id": dataTestId,
        "data-testid": dataTestId,
        "data-ardoq-popover-id": popoverId,
        children: [
          /* @__PURE__ */ jsx(
            ButtonContent,
            {
              $buttonSize: buttonSize,
              $isFlexible: isFlexible,
              $buttonType: buttonType,
              children: wrapTextNodes(buttonProps.children, { overflowMethod })
            }
          ),
          isLoading && /* @__PURE__ */ jsx(ButtonLoadingIconWrapper, { children: /* @__PURE__ */ jsx(SpinnerIcon, {}) })
        ]
      }
    );
  }
);
const generateButtonWithPredefinedProps = ({
  buttonType,
  buttonSize
}) => {
  const ForwardedComponent = forwardRef((buttonProps, ref) => /* @__PURE__ */ jsx(
    Button,
    {
      ref,
      buttonType,
      buttonSize,
      ...buttonProps
    }
  ));
  ForwardedComponent.displayName = `${capitalize(buttonType)}Button`;
  return ForwardedComponent;
};
const TertiaryButton = generateButtonWithPredefinedProps({
  buttonType: ButtonType.TERTIARY,
  buttonSize: ButtonSize.REGULAR
});
const SecondaryButton = generateButtonWithPredefinedProps({
  buttonType: ButtonType.SECONDARY,
  buttonSize: ButtonSize.REGULAR
});
const PrimaryButton = generateButtonWithPredefinedProps({
  buttonType: ButtonType.PRIMARY,
  buttonSize: ButtonSize.REGULAR
});
const DangerButton = generateButtonWithPredefinedProps({
  buttonType: ButtonType.DANGER,
  buttonSize: ButtonSize.REGULAR
});
const BrandButton = generateButtonWithPredefinedProps({
  buttonType: ButtonType.BRAND,
  buttonSize: ButtonSize.REGULAR
});
const GhostButton = generateButtonWithPredefinedProps({
  buttonType: ButtonType.GHOST,
  buttonSize: ButtonSize.REGULAR
});
const SmallButton = generateButtonWithPredefinedProps({
  buttonType: ButtonType.TERTIARY,
  buttonSize: ButtonSize.SMALL
});
const SmallDangerButton = generateButtonWithPredefinedProps({
  buttonType: ButtonType.DANGER,
  buttonSize: ButtonSize.SMALL
});
const SmallPrimaryButton = generateButtonWithPredefinedProps({
  buttonType: ButtonType.PRIMARY,
  buttonSize: ButtonSize.SMALL
});
const SmallSecondaryButton = generateButtonWithPredefinedProps({
  buttonType: ButtonType.SECONDARY,
  buttonSize: ButtonSize.SMALL
});
const SmallGhostButton = generateButtonWithPredefinedProps({
  buttonType: ButtonType.GHOST,
  buttonSize: ButtonSize.SMALL
});
const SmallTertiaryButton = generateButtonWithPredefinedProps({
  buttonType: ButtonType.TERTIARY,
  buttonSize: ButtonSize.SMALL
});
const ButtonGroup = styled(Space).attrs((props) => ({
  $justify: props.$alignToRight ? "end" : void 0,
  $align: "center"
}))``;
const IconButton = ({
  iconName,
  iconColor,
  ...buttonProps
}) => /* @__PURE__ */ jsx(Button, { buttonType: ButtonType.GHOST, ...buttonProps, children: /* @__PURE__ */ jsx(Icon, { iconName, color: iconColor }) });
export {
  BUTTON_SELECTOR,
  BrandButton,
  Button,
  ButtonGroup,
  ButtonSize,
  ButtonStyle,
  ButtonType,
  DangerButton,
  GhostButton,
  IconButton,
  MEDIUM_BUTTON_MINIMUM_HEIGHT,
  PrimaryButton,
  SecondaryButton,
  SmallButton,
  SmallDangerButton,
  SmallGhostButton,
  SmallPrimaryButton,
  SmallSecondaryButton,
  SmallTertiaryButton,
  TertiaryButton,
  UnstyledButton
};
//# sourceMappingURL=index.js.map
